import React from "react";
import { Container, Row, Col, Card } from "reactstrap";
import {
    Building2,
    Map,
    Droplets,
    Zap,
    Users,
    Cpu,
    FlaskConical,
    Mic,
    Brain,
    RefreshCw,
    Wrench,
} from "lucide-react";

const ATTENDEES = [
    { icon: Building2, label: "Infrastructure owners & operators", color: "#6bbac2" },
    { icon: Map, label: "Urban planners & policy leaders", color: "#bfbe28" },
    { icon: Droplets, label: "Water & wastewater authorities", color: "#6bbac2" },
    { icon: Zap, label: "Electricity distribution companies", color: "#bfbe28" },
    { icon: Users, label: "Māori and community representatives", color: "#6bbac2" },
    { icon: Cpu, label: "Technology providers & AI specialists", color: "#bfbe28" },
    { icon: FlaskConical, label: "Researchers & innovation leaders", color: "#6bbac2" },
];

const EXPERIENCE_ITEMS = [
    { icon: Mic, title: "High-impact keynote sessions", color: "#6bbac2" },
    { icon: Brain, title: "Deep-dive technical talks", color: "#bfbe28" },
    { icon: RefreshCw, title: "Interactive panels & roundtables", color: "#6bbac2" },
    { icon: Building2, title: "Focused exhibition showcasing live solutions", color: "#bfbe28" },
    { icon: Wrench, title: "Hands-on workshops for implementation pathways", color: "#6bbac2" },
];

function AttendAndExperienceSection() {
    const size =
        document.body.clientWidth >= 928 || document.documentElement.clientWidth >= 928;

    return (
        <section
            className="section py-5"
            style={{ background: "transparent", position: "relative", zIndex: 1 }}
        >
            <Container>
                <Row className="justify-content-center mb-5">
                    <Col xs={12}>
                        <img
                            src={require("assets/titleimages/3.jpg")}
                            alt="Conference experience"
                            style={{
                                width: "100%",
                                height: "auto",
                                maxHeight: "280px",
                                objectFit: "cover",
                                borderRadius: "16px",
                                border: "1px solid rgba(255,255,255,0.1)",
                                boxShadow: "0 12px 40px rgba(0,0,0,0.4)",
                            }}
                        />
                    </Col>
                </Row>
                <Row>
                    {/* Left: Who Should Attend */}
                    <Col lg={6} className="mb-4 mb-lg-0">
                        <h2
                            className="text-white mb-3"
                            style={{
                                fontSize: size ? "2rem" : "1.6rem",
                                fontWeight: 700,
                                letterSpacing: "0.06em",
                                textTransform: "uppercase",
                                color: "#6bbac2",
                            }}
                        >
                            Who Should Attend
                        </h2>
                        <div
                            style={{
                                width: "70px",
                                height: "4px",
                                background: "linear-gradient(90deg, #6bbac2, #bfbe28)",
                                borderRadius: "2px",
                                marginBottom: "1.5rem",
                            }}
                        />
                        <Row>
                            {ATTENDEES.map((item, i) => {
                                const Icon = item.icon;
                                return (
                                    <Col key={i} xs={12} className="mb-2">
                                        <Card
                                            style={{
                                                background: "rgba(255, 255, 255, 0.04)",
                                                backdropFilter: "blur(10px)",
                                                border: "1px solid rgba(255, 255, 255, 0.08)",
                                                borderRadius: "12px",
                                                padding: size ? "1rem 1.25rem" : "0.9rem 1rem",
                                                transition: "all 0.35s ease",
                                                cursor: "pointer",
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.background = `${item.color}15`;
                                                e.currentTarget.style.borderColor = `${item.color}50`;
                                                e.currentTarget.style.transform = "translateX(4px)";
                                                e.currentTarget.style.boxShadow = `0 8px 24px ${item.color}22`;
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.background = "rgba(255, 255, 255, 0.04)";
                                                e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.08)";
                                                e.currentTarget.style.transform = "translateX(0)";
                                                e.currentTarget.style.boxShadow = "none";
                                            }}
                                        >
                                            <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                                                <div
                                                    style={{
                                                        width: 40,
                                                        height: 40,
                                                        minWidth: 40,
                                                        borderRadius: "10px",
                                                        background: `${item.color}22`,
                                                        border: `1px solid ${item.color}50`,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                    }}
                                                >
                                                    <Icon size={20} strokeWidth={2} style={{ color: item.color }} />
                                                </div>
                                                <span
                                                    className="text-white"
                                                    style={{
                                                        fontSize: size ? "0.92rem" : "0.85rem",
                                                        fontWeight: 600,
                                                        lineHeight: "1.35",
                                                    }}
                                                >
                                                    {item.label}
                                                </span>
                                            </div>
                                        </Card>
                                    </Col>
                                );
                            })}
                        </Row>
                    </Col>

                    {/* Right: Conference Experience */}
                    <Col lg={6}>
                        <h2
                            className="text-white mb-3"
                            style={{
                                fontSize: size ? "2rem" : "1.6rem",
                                fontWeight: 700,
                                letterSpacing: "0.06em",
                                textTransform: "uppercase",
                                color: "#bfbe28",
                            }}
                        >
                            Conference Experience
                        </h2>
                        <div
                            style={{
                                width: "70px",
                                height: "4px",
                                background: "linear-gradient(90deg, #6bbac2, #bfbe28)",
                                borderRadius: "2px",
                                marginBottom: "0.75rem",
                            }}
                        />
                        <p
                            className="text-white mb-3"
                            style={{
                                fontSize: size ? "1.05rem" : "0.95rem",
                                lineHeight: "1.6",
                                opacity: 0.95,
                            }}
                        >
                            A carefully curated format designed for practical outcomes:
                        </p>
                        {EXPERIENCE_ITEMS.map((item, i) => {
                            const Icon = item.icon;
                            return (
                                <Card
                                    key={i}
                                    className="mb-2 experience-card"
                                    style={{
                                        background: "rgba(255, 255, 255, 0.04)",
                                        backdropFilter: "blur(10px)",
                                        border: "1px solid rgba(255, 255, 255, 0.08)",
                                        borderRadius: "14px",
                                        padding: size ? "1.1rem 1.25rem" : "1rem 1.1rem",
                                        transition: "all 0.35s ease",
                                        cursor: "pointer",
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.background = `${item.color}12`;
                                        e.currentTarget.style.borderColor = `${item.color}45`;
                                        e.currentTarget.style.transform = "translateX(4px)";
                                        e.currentTarget.style.boxShadow = `0 10px 28px ${item.color}22`;
                                        const iconWrap = e.currentTarget.querySelector(".experience-icon-wrap");
                                        if (iconWrap) {
                                            iconWrap.style.transform = "scale(1.08)";
                                            iconWrap.style.boxShadow = `0 4px 12px ${item.color}35`;
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background = "rgba(255, 255, 255, 0.04)";
                                        e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.08)";
                                        e.currentTarget.style.transform = "translateX(0)";
                                        e.currentTarget.style.boxShadow = "none";
                                        const iconWrap = e.currentTarget.querySelector(".experience-icon-wrap");
                                        if (iconWrap) {
                                            iconWrap.style.transform = "scale(1)";
                                            iconWrap.style.boxShadow = "none";
                                        }
                                    }}
                                >
                                    <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                                        <div
                                            className="experience-icon-wrap"
                                            style={{
                                                width: 46,
                                                height: 46,
                                                minWidth: 46,
                                                borderRadius: "12px",
                                                background: `linear-gradient(145deg, ${item.color}28, ${item.color}12)`,
                                                border: `2px solid ${item.color}50`,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                transition: "all 0.35s ease",
                                            }}
                                        >
                                            <Icon size={24} strokeWidth={2} style={{ color: item.color }} />
                                        </div>
                                        <span
                                            className="text-white"
                                            style={{
                                                fontSize: size ? "0.98rem" : "0.9rem",
                                                fontWeight: 600,
                                                lineHeight: "1.4",
                                            }}
                                        >
                                            {item.title}
                                        </span>
                                    </div>
                                </Card>
                            );
                        })}
                    </Col>
                </Row>
            </Container>
        </section>
    );
}

export default AttendAndExperienceSection;
